import React, { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

interface NavigationBlockerProps {
    when: boolean;
    message: (location: { pathname: string; hash: string }) => boolean | string;
}

/**
 * Replaces react-router v5's <Prompt>.
 *
 * Uses useBlocker (requires a data router) to intercept in-app SPA navigation.
 * The `message` callback follows the v5 Prompt convention:
 *   - return `true` → allow navigation
 *   - return a string → show window.confirm with that string
 */
export const NavigationBlocker: React.FC<NavigationBlockerProps> = ({ when, message }) => {
    const blocker = useBlocker(({ nextLocation }) => {
        if (!when) return false;
        const result = message({ pathname: nextLocation.pathname, hash: nextLocation.hash });
        if (result === true) return false;
        if (typeof result === 'string') return true;
        return false;
    });

    useEffect(() => {
        if (blocker.state === 'blocked') {
            const result = message({
                pathname: blocker.location.pathname,
                hash: blocker.location.hash,
            });

            if (result === true) {
                blocker.proceed();
                return;
            }

            if (typeof result === 'string') {
                if (window.confirm(result)) {
                    blocker.proceed();
                } else {
                    blocker.reset();
                }
                return;
            }

            blocker.proceed();
        }
    }, [blocker, message]);

    useEffect(() => {
        if (blocker.state === 'blocked' && !when) {
            blocker.reset();
        }
    }, [blocker, when]);

    return null;
};
